import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DailyMetric } from "@/lib/types";
import { compact, day, number } from "@/lib/format";

interface TooltipPayload {
  name?: string;
  value?: number;
  color?: string;
  dataKey?: string;
}

function TooltipBox({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="t-label">{label ? day(label) : ""}</div>
      {payload.map((item) => (
        <div key={item.dataKey ?? item.name} style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
          <span style={{ color: "#aab3c8" }}>{item.name}</span>
          <strong>{number(item.value ?? 0)}</strong>
        </div>
      ))}
    </div>
  );
}

export function ActivityChart({ data }: { data: DailyMetric[] }) {
  return (
    <div className="chart-box">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 6, bottom: 0, left: -18 }}>
          <defs>
            <linearGradient id="dauFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FF6B4A" stopOpacity={0.28} />
              <stop offset="100%" stopColor="#FF6B4A" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="newFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7C5CFF" stopOpacity={0.18} />
              <stop offset="100%" stopColor="#7C5CFF" stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E0D4" />
          <XAxis
            dataKey="day"
            tickFormatter={(value: string) => day(value)}
            tickLine={false}
            axisLine={false}
            minTickGap={28}
          />
          <YAxis
            tickFormatter={(value: number) => compact(value)}
            tickLine={false}
            axisLine={false}
            width={52}
          />
          <Tooltip content={<TooltipBox />} />
          <Area
            type="monotone"
            dataKey="dau"
            name="Daily active"
            stroke="#FF6B4A"
            strokeWidth={2.2}
            fill="url(#dauFill)"
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="new_users"
            name="New users"
            stroke="#7C5CFF"
            strokeWidth={1.8}
            fill="url(#newFill)"
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ReviewChart({ data }: { data: DailyMetric[] }) {
  return (
    <div className="chart-box-sm">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E0D4" />
          <XAxis
            dataKey="day"
            tickFormatter={(value: string) => day(value)}
            tickLine={false}
            axisLine={false}
            minTickGap={26}
          />
          <YAxis
            tickFormatter={(value: number) => compact(value)}
            tickLine={false}
            axisLine={false}
            width={52}
          />
          <Tooltip content={<TooltipBox />} />
          <Bar dataKey="reviews" name="Reviews" fill="#3DDC97" radius={[5, 5, 0, 0]} maxBarSize={26} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
